"use client";

import { withDataScope } from "@/app/context/DataScopeContext";
import { getDataDemandEndpoints, warmupDataDemandSources } from "@/lib/data-demand-endpoints";
import type { DataDemandSource } from "@/lib/view-data-needs";
import { apiFetchWithWarmup, cancelApiWarmups, startApiWarmup } from "./browser-warmup";
import { preloadWorkspaceEditorCode, preloadWorkspacePlanCode } from "@/lib/client/workspace-editor-code-loader";

type PageStartupPlan = {
  initialDataSources: DataDemandSource[];
  /** Показатели за период первой таблицы с календарём (lib/workspace-page-plan). */
  initialPeriodUrl?: string | null;
  initialPulseUrl?: string | null;
  scopeQuery: string;
  nodeTypes: string[];
};

let navigationVersion = 0;

/** Stop an abandoned page's preparation, including a late data-plan reply. */
export function cancelWorkspacePageWarmup(): void {
  navigationVersion++;
  cancelApiWarmups("sidebar");
}

function pageIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/page\/([^/?#]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Запускается на pointerdown по настоящей ссылке, не на hover. Повторный клик
 * отменяет ещё не понадобившиеся запросы прошлого перехода; одинаковые URL
 * объединяются общим browser-warmup и затем подхватываются SWR без дубля.
 */
export async function warmupWorkspacePageNavigation(pathname: string): Promise<void> {
  const pageId = pageIdFromPathname(pathname);
  if (!pageId) return;
  cancelWorkspacePageWarmup();
  const version = navigationVersion;
  // Редактор нужен любой странице-документу и может ехать одновременно с
  // компактным планом данных. Таблицу включим после ответа только при наличии.
  preloadWorkspaceEditorCode();
  const planUrl = `/api/workspace-pages/${encodeURIComponent(pageId)}/data-plan`;
  try {
    const plan = await apiFetchWithWarmup<PageStartupPlan>(planUrl);
    if (version !== navigationVersion) return;
    preloadWorkspacePlanCode(plan.nodeTypes);
    for (const source of warmupDataDemandSources(plan.initialDataSources ?? [])) {
      for (const endpoint of getDataDemandEndpoints(source)) {
        startApiWarmup(withDataScope(endpoint, plan.scopeQuery ?? ""), "sidebar");
      }
    }
    if (plan.initialPulseUrl) startApiWarmup(withDataScope(plan.initialPulseUrl, plan.scopeQuery ?? ""), "sidebar");
    if (plan.initialPeriodUrl) startApiWarmup(withDataScope(plan.initialPeriodUrl, plan.scopeQuery ?? ""), "sidebar");
  } catch {
    // Навигация не зависит от оптимизации: сама страница повторит план и запросы.
  }
}
